import React, { useState, useEffect, FC, useCallback } from "react";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import toast from "react-hot-toast";
import { axiosInstance } from "../../utility/apiInstance";
import { Link, useNavigate, useParams } from "react-router-dom";
import LoaderComponent from "../Loader";

const ResetPasswordPage: FC = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);
  const [passwordError, setPasswordError] = useState<string>("");
  const [isPasswordValid, setIsPasswordValid] = useState<boolean>(false);
  const [passwordSent, setPasswordSent] = useState<boolean>(false);
  const [verified, setVerified] = useState<number | null>(null);
  const { resetToken } = useParams();

  const token = resetToken;
  const verifyUserPassword = useCallback(async () => {
    try {
      await axiosInstance.get(
        `/api/v1/customers-auth/check-forgot-password/${token}`
      );
      setVerified(1);
    } catch (error: any) {
      if (error?.response.status === 403) {
        setVerified(0);
      }
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      verifyUserPassword();
    }
  }, [token, verifyUserPassword]);

  const validatePassword = (password: string) => {
    const minLength = 8;
    const hasLowercase = /[a-z]/.test(password);
    const hasUppercase = /[A-Z]/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (password.length < minLength) {
      setPasswordError("Password must be at least 8 characters long.");
      return false;
    }
    if (!hasLowercase || !hasUppercase || !hasSpecialChar) {
      setPasswordError(
        "Password must contain at least one lowercase letter, one uppercase letter, and one special character."
      );
      return false;
    }

    setPasswordError("");
    return true;
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    const isValid = validatePassword(newPassword);
    validatePasswords(newPassword, confirmPassword);
    setIsPasswordValid(isValid && newPassword === confirmPassword);
  };

  const handleConfirmPasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newConfirmPassword = e.target.value;
    setConfirmPassword(newConfirmPassword);
    validatePasswords(password, newConfirmPassword);
  };

  const validatePasswords = (password: string, confirmPassword: string) => {
    setIsPasswordValid(
      password === confirmPassword && validatePassword(password)
    );
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!isPasswordValid) {
      toast.error("Passwords do not match or are invalid.");
      return;
    }

    try {
      const response = await axiosInstance.post(
        `/api/v1/customers-auth/set-new-password/${token}`,
        { password }
      );

      if (response.data.success) {
        setPasswordSent(true);
        toast.success("Password reset successfully.");
        navigate("/auth/login");
      } else {
        toast.error("Failed to reset password.");
      }
    } catch (error) {
      toast.error("Error resetting password.");
    }
  };

  return (
    <>
      {verified === 0 ? (
        <div className="expiredmain">
          <div className="expiredbox">
            <figure className="expiredAnimation">
              <img src="/images/expired.png" alt="Expired Logo" />
            </figure>

            <h3>The link has expired. Please request a new one.</h3>

            <Link to="/auth/login" className="btnBox">
              Go to Homepage
            </Link>
          </div>
        </div>
      ) : verified === 1 ? (
        <div className="fd">
          <div className="sgateloginMain">
            <div className="sgateloginInner">
              <div className="sgLoginBox">
                <div className="sgLoginLeft">
                  <div className="sgLoginLeftinn">
                    <div className="sgLeftcontent">
                      <h1>
                        Welcome to <span className="blink">CargoDash</span>{" "}
                        Tracking!
                      </h1>

                      <p>
                        Your all-in-one solution for staying informed about your
                        shipment's progress. Receive intelligent notifications
                        through WhatsApp and Email. Begin tracking your shipment
                        today!
                      </p>
                    </div>
                    <figure className="slogintrack">
                      <img
                        src="/images/sgate-track.png"
                        alt="Track shipments"
                      />
                      <div className="circle-ripple"></div>
                    </figure>

                    <i className="logoicon">
                      <img src="/images/sgate-icon.png" alt="SGATE icon" />
                    </i>
                  </div>
                </div>
                <div className="sgLoginRight">
                  <div className="sgRightBox">
                    <figure className="slogo">
                      <img src="/images/logo.png" alt="SGATE logo" />
                    </figure>

                    <div className="sgLoginTittle">
                      <article>
                        {" "}
                        <i>
                          <img src="/images/signicon.png" alt="Sign icon" />
                        </i>
                        <h3> Change Your Password</h3>
                      </article>
                    </div>

                    <div className="loginform">
                      <div className="dd">
                        <form onSubmit={handleSubmit}>
                          <div className="mb-4">
                            <FormControl variant="outlined" fullWidth>
                              <InputLabel
                                htmlFor="outlined-adornment-password"
                                sx={{
                                  color: "#838285",
                                  "&.Mui-focused": {
                                    color: "#fab800",
                                  },
                                }}
                              >
                                Enter Password
                              </InputLabel>
                              <OutlinedInput
                                id="outlined-adornment-password"
                                type={showPassword ? "text" : "password"}
                                value={password}
                                onChange={handlePasswordChange}
                                inputProps={{
                                  maxLength: 20,
                                }}
                                endAdornment={
                                  <InputAdornment position="end">
                                    <IconButton
                                      aria-label="toggle password visibility"
                                      onClick={handleClickShowPassword}
                                      onMouseDown={handleMouseDownPassword}
                                      edge="end"
                                    >
                                      {showPassword ? (
                                        <Visibility />
                                      ) : (
                                        <VisibilityOff />
                                      )}
                                    </IconButton>
                                  </InputAdornment>
                                }
                                label="Enter Password"
                                sx={{
                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                    {
                                      borderColor: "#838285",
                                    },
                                }}
                              />
                            </FormControl>
                            {passwordError && (
                              <p className="text-red-600 text-sm">
                                {passwordError}
                              </p>
                            )}
                          </div>
                          <div className="mb-6">
                            <FormControl variant="outlined" fullWidth>
                              <InputLabel
                                htmlFor="outlined-adornment-confirm-password"
                                sx={{
                                  color: "#838285",
                                  "&.Mui-focused": {
                                    color: "#fab800",
                                  },
                                }}
                              >
                                Re-enter Password
                              </InputLabel>
                              <OutlinedInput
                                id="outlined-adornment-confirm-password"
                                type={showConfirmPassword ? "text" : "password"}
                                value={confirmPassword}
                                onChange={handleConfirmPasswordChange}
                                inputProps={{
                                  maxLength: 20,
                                }}
                                endAdornment={
                                  <InputAdornment position="end">
                                    <IconButton
                                      aria-label="toggle password visibility"
                                      onClick={handleClickShowConfirmPassword}
                                      onMouseDown={handleMouseDownPassword}
                                      edge="end"
                                    >
                                      {showConfirmPassword ? (
                                        <Visibility />
                                      ) : (
                                        <VisibilityOff />
                                      )}
                                    </IconButton>
                                  </InputAdornment>
                                }
                                label="Re-enter Password"
                                sx={{
                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                    {
                                      borderColor: "#838285",
                                    },
                                }}
                              />
                            </FormControl>
                            {confirmPassword !== password && (
                              <p className="text-red-600 text-sm">
                                Passwords do not match
                              </p>
                            )}
                          </div>
                          <div className="logibbtn">
                            <Button
                              variant="contained"
                              type="submit"
                              fullWidth
                              disabled={!isPasswordValid}
                              className="btn btnTheme"
                            >
                              Reset Password
                            </Button>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <LoaderComponent />
      )}
    </>
  );
};

export default ResetPasswordPage;
